/**
 * Utility functions for video thumbnail generation and manipulation
 */

export interface VideoThumbnailOptions {
  timeOffset?: number // Time in seconds to capture thumbnail (default: 2)
  quality?: number // JPEG quality 0-1 (default: 0.8)
  width?: number // Thumbnail width (default: video width)
  height?: number // Thumbnail height (default: video height)
}

/**
 * Generate a thumbnail from a video element
 */
export const generateVideoThumbnail = async (
  videoElement: HTMLVideoElement,
  options: VideoThumbnailOptions = {}
): Promise<string> => {
  const {
    timeOffset = 2,
    quality = 0.8,
    width = videoElement.videoWidth,
    height = videoElement.videoHeight
  } = options

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      reject(new Error('Canvas context not available'))
      return
    }

    // Set canvas dimensions
    canvas.width = width
    canvas.height = height

    // Store original time
    const originalTime = videoElement.currentTime

    // Seek to desired time
    videoElement.currentTime = Math.min(timeOffset, videoElement.duration * 0.1)

    const handleSeeked = () => {
      videoElement.removeEventListener('seeked', handleSeeked)
      
      try {
        // Draw video frame to canvas
        ctx.drawImage(videoElement, 0, 0, width, height)
        
        // Convert to data URL
        const thumbnailDataUrl = canvas.toDataURL('image/jpeg', quality)
        
        // Restore original time
        videoElement.currentTime = originalTime
        
        resolve(thumbnailDataUrl)
      } catch (error) {
        reject(error)
      }
    }

    const handleError = () => {
      videoElement.removeEventListener('seeked', handleSeeked)
      videoElement.removeEventListener('error', handleError)
      reject(new Error('Failed to seek video'))
    }

    videoElement.addEventListener('seeked', handleSeeked)
    videoElement.addEventListener('error', handleError)
  })
}

/**
 * Generate multiple thumbnails from a video at different time points
 */
export const generateVideoThumbnails = async (
  videoElement: HTMLVideoElement,
  timePoints: number[] = [1, 3, 5, 10],
  options: Omit<VideoThumbnailOptions, 'timeOffset'> = {}
): Promise<string[]> => {
  const thumbnails: string[] = []
  
  for (const timePoint of timePoints) {
    try {
      const thumbnail = await generateVideoThumbnail(videoElement, {
        ...options,
        timeOffset: timePoint
      })
      thumbnails.push(thumbnail)
    } catch (error) {
      console.warn(`Failed to generate thumbnail at ${timePoint}s:`, error)
    }
  }
  
  return thumbnails
}

/**
 * Create a looping preview video from a longer video
 * This is a placeholder - in production, you'd use a video processing service
 */
export const createLoopingPreview = async (
  videoUrl: string,
  startTime: number = 0,
  duration: number = 5
): Promise<string> => {
  // In a real implementation, you would:
  // 1. Use FFmpeg.wasm or a server-side service
  // 2. Extract a short segment from the video
  // 3. Create a looping version
  // 4. Return the URL of the processed video
  
  console.log('Creating looping preview:', { videoUrl, startTime, duration })
  
  // For now, return the original URL
  // In production, implement actual video processing
  return videoUrl
}

/**
 * Check if a video URL is a YouTube URL
 */
export const isYouTubeUrl = (url: string): boolean => {
  return url.includes('youtube.com') || url.includes('youtu.be')
}

/**
 * Extract YouTube video ID from URL
 */
export const extractYouTubeId = (url: string): string | null => {
  const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
  const match = url.match(regex)
  return match ? match[1] : null
}

/**
 * Generate YouTube thumbnail URL
 */
export const getYouTubeThumbnail = (url: string, quality: 'default' | 'medium' | 'high' | 'standard' | 'maxres' = 'high'): string | null => {
  const videoId = extractYouTubeId(url)
  if (!videoId) return null
  
  return `https://img.youtube.com/vi/${videoId}/${quality}default.jpg`
}

/**
 * Preload video metadata
 */
export const preloadVideoMetadata = (videoUrl: string): Promise<HTMLVideoElement> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.crossOrigin = 'anonymous'
    
    video.onloadedmetadata = () => resolve(video)
    video.onerror = () => reject(new Error('Failed to load video metadata'))
    
    video.src = videoUrl
  })
}

/**
 * Convert Firebase Storage URL to a CORS-friendly URL
 * This helps bypass CORS issues by using a proxy or direct download URL
 */
export const getCorsProxyUrl = (url: string): string => {
  if (!url || !url.includes('firebasestorage.googleapis.com')) {
    return url
  }

  try {
    const urlObj = new URL(url)

    // Ensure alt=media is present for direct file access
    if (urlObj.searchParams.get('alt') !== 'media') {
      urlObj.searchParams.set('alt', 'media')
    }
    
    // We assume the token is already present in the URL from Firestore
    // and avoid adding/modifying it.

    return urlObj.toString()
  } catch (error) {
    console.error('Invalid URL provided to getCorsProxyUrl:', url, error)
    return url // Return original url if parsing fails
  }
}

